import { router } from '@inertiajs/react'
import { Button } from '@/components/ui/button'
import { Heart, ArrowLeft } from 'lucide-react'

export default function Privacy() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950">
      {/* Navigation Bar */}
      <nav className="sticky top-0 z-50 border-b bg-white/80 backdrop-blur-sm dark:bg-gray-950/80 dark:border-gray-800">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-blue-600 to-indigo-600">
                <Heart className="h-6 w-6 text-white" />
              </div>
              <span className="text-xl font-semibold">Breakthrough Health</span>
            </div>
            <Button variant="ghost" onClick={() => router.visit('/')}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Home
            </Button>
          </div>
        </div>
      </nav>

      {/* Content Section */}
      <section className="mx-auto max-w-4xl px-4 py-20 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h1 className="text-4xl font-bold tracking-tight sm:text-5xl mb-6">
            Privacy Policy
          </h1>
          <div className="h-1 w-24 mx-auto bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 rounded-full"></div>
        </div>

        <div className="prose prose-lg dark:prose-invert max-w-none">
          <div className="space-y-6 text-lg leading-relaxed text-foreground">
            <div className="p-6 bg-blue-50 dark:bg-blue-950/30 border-l-4 border-blue-600 rounded-r-lg">
              <p className="font-semibold mb-2">Your Privacy Matters</p>
              <p>
                At Breakthrough Health, we are committed to protecting your privacy and ensuring the security of your personal health information in accordance with HIPAA regulations.
              </p>
            </div>

            <section>
              <h2 className="text-2xl font-bold mb-4">HIPAA Compliance</h2>
              <p className="mb-4">
                Breakthrough Health is fully compliant with the Health Insurance Portability and Accountability Act (HIPAA) and all applicable privacy regulations. We implement administrative, physical, and technical safeguards to protect your protected health information (PHI).
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">Information We Collect</h2>
              <p className="mb-4">
                As a healthcare coordination platform, we collect and process the following types of information:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Personal identification information (name, date of birth, contact details)</li>
                <li>Medical history and health assessments</li>
                <li>Laboratory results and clinical observations</li>
                <li>Treatment plans and medication records</li>
                <li>Care coordination notes and follow-up information</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">How We Use Your Information</h2>
              <p className="mb-4">
                Your information is used solely for the purposes of:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Coordinating your Hepatitis C treatment and care</li>
                <li>Communicating with your healthcare providers</li>
                <li>Tracking your progress through the care continuum</li>
                <li>Ensuring continuity of care and follow-up</li>
                <li>Meeting regulatory and quality reporting requirements</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">Information Sharing</h2>
              <p className="mb-4">
                We only share your information with:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Healthcare providers directly involved in your care</li>
                <li>Authorized clinic staff managing your treatment</li>
                <li>Business associates bound by HIPAA agreements</li>
                <li>Parties you have specifically authorized</li>
                <li>Entities as required by law or regulation</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">Data Security</h2>
              <p className="mb-4">
                We implement industry-standard security measures including:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Encrypted data transmission and storage</li>
                <li>Access controls and user authentication</li>
                <li>Regular security audits and monitoring</li>
                <li>Comprehensive audit trails for all data access</li>
                <li>Staff training on privacy and security protocols</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">Your Rights</h2>
              <p className="mb-4">
                You have the right to:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Access and review your health information</li>
                <li>Request corrections to your records</li>
                <li>Request restrictions on certain uses and disclosures</li>
                <li>Receive an accounting of disclosures</li>
                <li>Request confidential communications</li>
                <li>File a complaint if you believe your privacy rights have been violated</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">Contact Us</h2>
              <p className="mb-4">
                For questions about our privacy practices or to exercise your privacy rights, please contact your clinic's privacy officer or reach out to our support team.
              </p>
            </section>

            <section className="mt-8 p-6 bg-blue-50 dark:bg-blue-950/30 rounded-lg">
              <p className="text-sm text-muted-foreground">
                Last updated: January 2025
              </p>
            </section>
          </div>
        </div>

        {/* CTA Section */}
        <div className="mt-16 text-center">
          <Button size="lg" onClick={() => router.visit('/')}>
            Back to Home
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t bg-white/50 backdrop-blur-sm dark:bg-gray-950/50 dark:border-gray-800 mt-20">
        <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center gap-4 text-center text-sm text-muted-foreground md:flex-row md:justify-between">
            <p>&copy; 2026 Breakthrough Health. Empowering community health through technology.</p>
            <div className="flex gap-6">
              <button
                onClick={() => router.visit('/about')}
                className="hover:text-foreground transition-colors"
              >
                About Us
              </button>
              <button
                onClick={() => router.visit('/disclaimer')}
                className="hover:text-foreground transition-colors"
              >
                Disclaimer
              </button>
              <button
                onClick={() => router.visit('/privacy')}
                className="hover:text-foreground transition-colors"
              >
                Privacy Policy
              </button>
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}
